import React from 'react';
import type { ReactNode, Ref } from 'react';
import styled from 'styled-components';
import { useObserver } from 'mobx-react-lite';
import { useTheme } from '@material-ui/core/styles';

import { mediaQueries } from 'shared/styles';

import useUI from 'src/hooks/use-ui';
import useViewport from 'src/hooks/use-viewport';
import { useIsMobile } from 'src/hooks/use-is-mobile';
import { BannerSlideWrapper } from 'src/components/menu-image-banner';
import { Typography } from 'src/typography';

// Shared home-carousel artwork approved for both incentive treatments.
const ARTWORK_DESKTOP_SRC = '/images/pay-by-bank-cash-back-home-artwork-desktop.png';
const ARTWORK_MOBILE_SRC = '/images/pay-by-bank-cash-back-home-artwork-mobile.png';

// Selects the --dcm-<family>-home-banner-* custom property family so each
// treatment keeps an independent SDK theming surface with identical fallbacks.
export type IncentiveHomeBannerCustomizationPrefix = 'cash-back' | 'points-boost';

export type IncentiveHomeBannerProps = {
  headline: ReactNode;
  body: ReactNode;
  disclaimer?: ReactNode;
  ctaLabel?: string;
  isActionable: boolean;
  onCta?: () => void;
  ctaAriaLabel?: string;
  ariaLabel: string;
  testId: string;
  customizationPrefix: IncentiveHomeBannerCustomizationPrefix;
  bannerRef?: Ref<HTMLElement>;
};

export const IncentiveHomeBanner = ({
  headline,
  body,
  disclaimer,
  ctaLabel,
  isActionable,
  onCta,
  ctaAriaLabel,
  ariaLabel,
  testId,
  customizationPrefix,
  bannerRef,
}: IncentiveHomeBannerProps): JSX.Element => {
  const UI = useUI();
  const viewport = useViewport();
  const theme = useTheme();
  const isIframe = useObserver(() => UI.isIframe);
  const isMobileViewport = useIsMobile();
  // Embedded menus report the host viewport separately; match MenuImage's artwork selection.
  const isMobile = isIframe && viewport.width > 0 ? viewport.width < theme.breakpoints.values.sm : isMobileViewport;

  return (
    <BannerSlideWrapper onClick={isActionable ? onCta : undefined} ariaLabel={isActionable ? ctaAriaLabel : undefined}>
      <Banner ref={bannerRef} aria-label={ariaLabel} data-testid={testId} $prefix={customizationPrefix}>
        <TextContent $prefix={customizationPrefix}>
          <Headline tag='h2' size='xlarge' $prefix={customizationPrefix}>
            {headline}
          </Headline>
          <Body size='xlarge' tag='p'>
            {body}
          </Body>
          {isActionable && ctaLabel && <LinkBankLabel $prefix={customizationPrefix}>{ctaLabel}</LinkBankLabel>}
          {disclaimer && (
            <Disclaimer size='large' tag='p'>
              {disclaimer}
            </Disclaimer>
          )}
        </TextContent>
        <Artwork aria-hidden='true'>
          <ArtworkImage src={isMobile ? ARTWORK_MOBILE_SRC : ARTWORK_DESKTOP_SRC} alt='' />
        </Artwork>
      </Banner>
    </BannerSlideWrapper>
  );
};

type PrefixProps = { $prefix: IncentiveHomeBannerCustomizationPrefix };

const Banner = styled.section<PrefixProps>`
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden;
  width: 100%;
  container-type: inline-size;
  aspect-ratio: 3 / 1;
  border-radius: ${({ $prefix }) => `var(--dcm-${$prefix}-home-banner-radius, var(--dct--customized-radius-lg, 12px))`};
  background-color: ${({ $prefix, theme }) => `var(--dcm-${$prefix}-home-banner-background, ${theme.colors.green20})`};

  @media ${mediaQueries.largePhone} {
    aspect-ratio: 2.22 / 1;
  }
`;

const TextContent = styled.div<PrefixProps>`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: clamp(6px, 1.333cqw, 16px);
  max-width: 55%;
  padding: clamp(12px, 3.333cqw, 40px) clamp(12px, 2.667cqw, 32px);
  color: ${({ $prefix, theme }) => `var(--dcm-${$prefix}-home-banner-text-color, ${theme.colors.green90})`};
`;

const Headline = styled(Typography.Heading)<PrefixProps>`
  margin: 0;
  font-size: ${({ $prefix }) => `var(--dcm-${$prefix}-home-banner-headline-size, clamp(16px, 4cqw, 48px))`};
`;

const Body = styled(Typography.Body)`
  display: none;
  font-size: clamp(12px, 1.5cqw, 18px);
  line-height: 1.3;

  ${({ theme }) => theme.breakpoints.up('md')} {
    display: block;
  }
`;

const Disclaimer = styled(Typography.Label)`
  font-size: clamp(10px, 1.167cqw, 14px);
  line-height: 1.2;
`;

const LinkBankLabel = styled.span<PrefixProps>`
  padding: clamp(6px, 0.667cqw, 8px) clamp(12px, 1.667cqw, 20px);
  border-radius: ${({ $prefix }) => `var(--dcm-${$prefix}-home-banner-button-radius, 999px)`};
  background-color: ${({ $prefix, theme }) =>
    `var(--dcm-${$prefix}-home-banner-button-background, ${theme.colors.green90})`};
  color: ${({ $prefix, theme }) => `var(--dcm-${$prefix}-home-banner-button-color, ${theme.colors.white})`};
  font-size: ${({ $prefix }) => `var(--dcm-${$prefix}-home-banner-button-font-size, clamp(12px, 1.333cqw, 16px))`};
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
`;

const Artwork = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: auto;
  height: 100%;
  display: block;
`;

const ArtworkImage = styled.img`
  width: auto;
  height: 100%;
  display: block;
`;
